import React, { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { trpc } from '@klear/ui/lib/trpc'
import { Input } from '@klear/ui/components/input'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { buttonVariants } from '@klear/ui/components/button-link'
import { cn } from '@klear/ui/lib/utils'
import { useDebouncedValue } from '@klear/ui/hooks/use-debounced-value'
import { SUBSCRIPTION_TIER_LAUNCH } from '@klear/constants/subscription-tier'
import { KlearDate } from '@klear/date'

const STRIPE_DASHBOARD = 'https://dashboard.stripe.com'
const PANEL_CLASS = 'flex flex-col gap-2 rounded-md border border-dashed border-border p-3 bg-muted/50'

const Detail: React.FC<{ label: string; value: string | null }> = ({ label, value }) => {
  if (!value) {
    return null
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}:</span>
      <span className="text-sm">{value}</span>
    </div>
  )
}

const TraderSubscriptionPanel: React.FC<{ traderId: number }> = ({ traderId }) => {
  const [isLinking, setIsLinking] = useState(false)
  const [query, setQuery] = useState('')
  const utils = trpc.useUtils()

  const billing = trpc.subscription.billingByTraderId.useQuery({ traderId })
  const debouncedQuery = useDebouncedValue(query)
  const search = trpc.subscription.searchStripeCustomers.useQuery(
    { query: debouncedQuery },
    { enabled: debouncedQuery.trim().length >= 2 }
  )
  const linkCustomer = trpc.subscription.linkStripeCustomer.useMutation({
    onSuccess: () => {
      setIsLinking(false)
      setQuery('')
      utils.subscription.billingByTraderId.invalidate({ traderId })
    }
  })

  const customerId = billing.data?.customerId ?? null
  const subscriptionId = billing.data?.subscriptionId ?? null
  const isOwed = billing.data?.isOwed ?? false
  const amount = billing.data?.amount
    ? `${billing.data.amount.toString()} ${billing.data.frequency ?? ''}`.trim()
    : null
  const manageClass = cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'h-7 gap-1 px-2 text-xs')

  if (billing.isLoading) {
    return (
      <div className={PANEL_CLASS}>
        <span className="text-sm text-muted-foreground">Subscription</span>
        <span className="text-xs text-muted-foreground">Loading…</span>
      </div>
    )
  }

  return (
    <div className={PANEL_CLASS}>
      <span className="text-sm text-muted-foreground">Subscription</span>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Tier:</span>
        <span className="text-base font-medium">{billing.data?.tier || '—'}</span>
        {billing.data?.status && (
          <Badge variant={isOwed ? 'destructive' : 'secondary'} className="capitalize">
            {billing.data.status.replace(/_/g, ' ')}
          </Badge>
        )}
        {subscriptionId && (
          <a
            href={`${STRIPE_DASHBOARD}/subscriptions/${subscriptionId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={manageClass}
          >
            manage <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      <Detail label="Amount" value={amount} />
      <Detail label="Started" value={billing.data?.startDate ? KlearDate.format(billing.data.startDate) : null} />
      {billing.data?.renewalDate && (
        <Detail label={billing.data.renewalDateLabel} value={KlearDate.format(billing.data.renewalDate)} />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Customer Account:</span>
        {customerId ? (
          <span className="font-mono text-sm">{customerId}</span>
        ) : (
          <span className="text-sm text-muted-foreground">not linked</span>
        )}
        {!isLinking && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={() => setIsLinking(true)}
          >
            edit
          </Button>
        )}
        {customerId && (
          <a
            href={`${STRIPE_DASHBOARD}/customers/${customerId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={manageClass}
          >
            manage <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {isOwed && subscriptionId && (
        <a
          href={`${STRIPE_DASHBOARD}/subscriptions/${subscriptionId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs font-medium text-destructive underline"
        >
          {billing.data?.status === 'unpaid'
            ? 'Unpaid — Stripe stopped retrying; open subscription in Stripe'
            : 'Past due — open subscription in Stripe'}
          <ExternalLink className="h-3 w-3" />
        </a>
      )}

      {customerId && !subscriptionId && (
        <span className="text-xs text-muted-foreground">
          {billing.data?.tier === SUBSCRIPTION_TIER_LAUNCH
            ? 'No entitling Stripe subscription — reporting as Launch. A tier still stamped on the customer record is ignored once its subscriptions have lapsed.'
            : 'No active Stripe subscription — tier resolves from the customer record (the annual invoice cohort).'}
        </span>
      )}

      {isLinking && (
        <div>
          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="Search Klear Stripe customers by name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault()
              }}
              autoFocus
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsLinking(false)
                setQuery('')
              }}
            >
              Cancel
            </Button>
          </div>
          {search.isFetching && <div className="text-xs text-muted-foreground mt-1">Searching…</div>}
          {search.data && search.data.length === 0 && debouncedQuery.trim().length >= 2 && !search.isFetching && (
            <div className="text-xs text-muted-foreground mt-1">No matching customers.</div>
          )}
          {search.data && search.data.length > 0 && (
            <ul className="mt-2 divide-y divide-border rounded-md border border-border bg-background">
              {search.data.map((customer) => (
                <li key={customer.id} className="flex items-center justify-between gap-2 p-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{customer.name ?? '(no name)'}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {customer.email ?? '—'} · {customer.id}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={linkCustomer.isPending || customer.id === customerId}
                    onClick={() => linkCustomer.mutate({ traderId, customerId: customer.id })}
                  >
                    {customer.id === customerId ? 'Linked' : 'Link'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {linkCustomer.isError && (
            <p className="text-xs text-destructive mt-1">
              {linkCustomer.error.message || "Couldn't link that customer. Try again."}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default TraderSubscriptionPanel
